/**
 * @module Utils
 *
 * Role rules shared by the sidebar menu and the router (CN-2813). A page is
 * reachable by URL exactly when its menu entry is visible, because the menu's
 * displayIf and the route's `access` read the same predicate. Only rules that
 * depend on the account alone belong here: a rule that waits on a later fetch
 * (the facility, its Stripe flag) would send users away while it loads, so
 * those stay in the sidebar as display-only rules.
 */
import { RoleType, UserContextType } from "app/contexts/UserContext";
import { hasFeature } from "app/utils/hasFeature";

export type RoleAccess = (user: UserContextType) => boolean;

/** Facility admins and coaches; never schedulers. */
export const showForAdminAndOrCoach: RoleAccess = (user) =>
  [RoleType.ADMIN, RoleType.COACH].includes(user.role);

/** Reports: admins and coaches on the scheduling product. */
export const showForSchedulingAdminsOrCoaches: RoleAccess = (user) =>
  !!hasFeature(user.features, "scheduling") && showForAdminAndOrCoach(user);

/**
 * The coach workspace (spaces, groups, feed, resources, coach billing…):
 * everyone except the admin staff, facility Admin and Scheduler, of a facility
 * on the scheduling product. Athletes and coaches pass, with or without a
 * facility.
 */
export const showForNonFacilityAdminStaff: RoleAccess = (user) => {
  const isFacilityUser =
    !!hasFeature(user.features, "scheduling") && !!user.facilityMembershipId;
  if (!isFacilityUser) {
    return true;
  }
  const isAdminStaff = [RoleType.ADMIN, RoleType.SCHEDULER].includes(user.role);
  return !isAdminStaff;
};

/** Facility Admin or Coach Admin: manage facility, facility billing. */
export const isFacilityAdminOrCoachAdmin: RoleAccess = (user) =>
  user.role === RoleType.ADMIN || user.behavesLikeAdmin === true;

/**
 * Staff of a facility: Admin, Coach or Scheduler. This rule opens the
 * read-only Resources page. The page checks the facility flag, because the
 * facility loads after the account.
 */
export const isFacilityStaff: RoleAccess = (user) =>
  !!user.facilityMembershipId &&
  [RoleType.ADMIN, RoleType.COACH, RoleType.SCHEDULER].includes(user.role);
